/**
 * The platform-role dialog (f3.1).
 *
 * **Driven entirely by the catalogue.** One `option` per role f2.2's
 * `PLATFORM_ROLE` lookup returned, in its `sort_order`, labelled with its
 * `name`. No role is named in this file, which is what makes f10.1's and
 * f16.2's acceptance criterion -- *"exists as a lookup row, not a migration, and
 * is grantable by an Admin"* -- true here with no edit at all.
 *
 * **A select, not a stack of radio cards.** One role is held at a time and the
 * catalogue grows by insert, so the control has to stay readable as roles are
 * added in phases 10 and 16 -- a list of cards does not, and it buried the
 * user's CURRENT role among identical-looking siblings. A select opens showing
 * exactly what they hold today, which is the question an Admin opens this
 * dialog to answer.
 *
 * The prototype's `Business Unit Group scope` select is **absent**, not
 * disabled: f1.3 D2 dropped the one scoped role, so there is nothing for it to
 * scope, and a permanently disabled control invites someone to enable it.
 *
 * A refusal keeps the dialog open with the selection retained, because a `409`
 * from the last-Admin census is a fact about the platform rather than a typo,
 * and closing the dialog would discard the Admin's intent along with it.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';

import { ApiError, type LookupValue, type UserAccount } from '@/api/client';
import { useUpdateUser } from '@/api/queries';

export function RoleDialog({
  user,
  roles,
  onClose,
}: {
  user: UserAccount;
  /** Every role the catalogue returned, in registry order. */
  roles: readonly LookupValue[];
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const update = useUpdateUser();

  const current = roles.find((role) => role.code === user.role.code);
  const [selected, setSelected] = useState<string>(current?.id ?? '');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    panel.current?.querySelector<HTMLElement>('select')?.focus();
  }, []);

  // A role the catalogue has deactivated is not OFFERED, but if this user
  // already holds it, it stays in the list as the current selection -- f2.2's
  // consumer rule: offer active values, always render a stored one.
  const offered = roles.filter((role) => role.is_active || role.id === current?.id);

  const unchanged = selected === (current?.id ?? '');

  const submit = () => {
    if (unchanged || !selected) return;
    setMessage(null);
    update.mutate(
      { userId: user.id, change: { platform_role_id: selected } },
      {
        onSuccess: onClose,
        onError: (error) => {
          // 400 and 409 both render HERE, in the open dialog. A 403 is a
          // refusal, not an expired session: f1.2's interactive
          // re-authentication is for 401 and only 401.
          setMessage(
            error instanceof ApiError ? error.message : 'The change could not be saved.',
          );
        },
      },
    );
  };

  return (
    <div className="modal-scrim" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-label="Platform roles" ref={panel}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="modal-head">
            <div>
              <h3>Platform roles</h3>
              <div className="m-sub">
                {user.display_name} &middot; {user.email}
              </div>
            </div>
            <button
              type="button"
              className="icon-btn"
              onClick={onClose}
              aria-label="Close"
              disabled={update.isPending}
            >
              <X aria-hidden="true" />
            </button>
          </div>

          <div className="modal-body">
            {message && (
              <div className="alert alert-danger users-dialog-alert" role="alert">
                <div>
                  <div className="a-body">{message}</div>
                </div>
              </div>
            )}

            <div className="field mb-4">
              <label htmlFor="user-platform-role">Platform role</label>
              {/* Opens on the role the account holds today, because `selected`
                  starts as the catalogue id matching `user.role.code`. */}
              <select
                id="user-platform-role"
                value={selected}
                disabled={update.isPending}
                onChange={(event) => setSelected(event.target.value)}
              >
                {/* Only reachable for an account whose stored role is missing
                    from the catalogue entirely; a blank select would otherwise
                    look like a role nobody set. */}
                {!current && <option value="">No role on record</option>}
                {offered.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.is_active
                      ? role.name
                      : `${role.name} (deactivated in reference data)`}
                  </option>
                ))}
              </select>
              <span className="hint">
                Currently {user.role.name}. One role per account.
              </span>
            </div>
          </div>

          <div className="modal-foot">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={update.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={update.isPending || unchanged}
            >
              {update.isPending ? (
                <Loader2 className="spin" aria-hidden="true" />
              ) : (
                <Check aria-hidden="true" />
              )}
              Save role
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Deactivation is confirmed; reactivation is not.
 *
 * Reactivation grants nothing the account did not already have, so a
 * confirmation there would be ceremony. Deactivation denies every request the
 * person makes, so it is worth a sentence -- including the one that says no data
 * is deleted, because "deactivate" reads like "delete" to most people.
 */
export function DeactivateDialog({
  user,
  onClose,
}: {
  user: UserAccount;
  onClose: () => void;
}) {
  const update = useUpdateUser();
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const submit = () => {
    setMessage(null);
    update.mutate(
      { userId: user.id, change: { is_active: false } },
      {
        onSuccess: onClose,
        onError: (error) =>
          setMessage(
            error instanceof ApiError ? error.message : 'The change could not be saved.',
          ),
      },
    );
  };

  return (
    <div className="modal-scrim" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-label="Deactivate account">
        <div className="modal-head">
          <div>
            <h3>Deactivate account</h3>
            <div className="m-sub">
              {user.display_name} &middot; {user.email}
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="Close"
            disabled={update.isPending}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">
          {message && (
            <div className="alert alert-danger users-dialog-alert" role="alert">
              <div>
                <div className="a-body">{message}</div>
              </div>
            </div>
          )}
          <p>
            {user.display_name} will be denied every request until the account is
            reactivated, even while signed in.
          </p>
          <p className="hint">
            No data is deleted. The account keeps its role and its history, and
            reactivating it restores access exactly as it was.
          </p>
        </div>
        <div className="modal-foot">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={update.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={submit}
            disabled={update.isPending}
          >
            {update.isPending && <Loader2 className="spin" aria-hidden="true" />}
            Deactivate
          </button>
        </div>
      </div>
    </div>
  );
}
